// Why a run ended, what it lacked and what to do next, in the reader's
// language (P-095 slice 8, agion/Agion#2939; per stored code,
// agion/Agion#2947). The words come from the run's end record as orchestration
// serves it (GET /v1/executions/{id}: `result` and `codes`, present only with
// MISSION_RUN_RESULT_ENABLED on). The server decides the result and stores
// each code with its effect, default cause and whether the run recovered; the
// chat picks the code the server's resolver reads first (the first unresolved
// failure, else the first unresolved limitation) and maps it to words through
// END_WORDS, keyed by the registry's codes. The end sentence never holds the
// code; the chat shows the stored code beside it in small text. Without a
// result nothing is shown, so the chat keeps today's end lines. The console's
// twin is admin-ui src/services/run-end.ts.
import { isRunEndRegistryCode, isStoreFailureReason, type RunEndRegistryCode, type StoreFailureReason } from '../generated/runEndCodes'

/** One code of the run's end record (orchestration StoredCode), as served. */
export interface RunEndCode {
  code: string
  effect: string
  cause: string
  resolved: boolean
  /** The detail the code's source named beside it (agion/Agion#3457), e.g. host_store_full; absent when the server stored none. */
  reason?: string
}

export type RunResult = 'completed' | 'completed_with_limitations' | 'failed' | 'cancelled'

const RUN_RESULTS: readonly string[] = ['completed', 'completed_with_limitations', 'failed', 'cancelled']

/** The run's end record: the server's result and its codes, in the stored order. */
export interface RunEndRecord {
  result: RunResult
  codes: RunEndCode[]
}

const K = 'chat:runEnd'

/** The registry's cause categories (contract/workflow Cause) and their word keys. */
const CAUSE_KEYS = {
  agent: 'agent',
  person: 'person',
  environment: 'environment',
  platform: 'platform',
  'mission design': 'missionDesign',
} as const

/**
 * Codes with no run ending term whose own cause words say more than their
 * category: a wait nobody answered, an expired approval among them
 * (agion/Agion#1964; the stored code does not say what the wait was for).
 */
const CODE_CAUSE_KEYS = {
  HUMAN_NO_RESPONSE: 'noAnswer',
} as const

/** A cause's word key: a code's own, a registry category's, or unknown. */
type CauseKey = (typeof CAUSE_KEYS)[keyof typeof CAUSE_KEYS] | (typeof CODE_CAUSE_KEYS)[keyof typeof CODE_CAUSE_KEYS] | 'unknown'

function causeKeyOf(first: RunEndCode | undefined): CauseKey {
  if (!first) return 'unknown'
  if (Object.prototype.hasOwnProperty.call(CODE_CAUSE_KEYS, first.code)) return CODE_CAUSE_KEYS[first.code as keyof typeof CODE_CAUSE_KEYS]
  if (Object.prototype.hasOwnProperty.call(CAUSE_KEYS, first.cause)) return CAUSE_KEYS[first.cause as keyof typeof CAUSE_KEYS]
  return 'unknown'
}

/**
 * How the end line words each registry code that fails or limits a run.
 * The key set is the registry's (generated runEndCodes.ts), so a code the
 * registry adds fails the typecheck here until its words are chosen:
 * - 'words': its own cause and next step, `${K}.code.<CODE>.title` and `.next`;
 * - 'next': its run ending term (nextActivity.runEnding) as the cause, else
 *   its cause category, and its own next step, `${K}.code.<CODE>.next`;
 * - 'cause': its run ending term, else its cause category, as the cause, and
 *   the category's next step.
 */
const END_WORDS = {
  CONTEXT_WINDOW_EXHAUSTED: 'cause',
  LLM_DEADLINE: 'cause',
  MISSION_BUDGET_EXCEEDED: 'cause',
  PROVIDER_UNAVAILABLE: 'cause',
  PROVIDER_QUOTA_EXHAUSTED: 'next',
  MODEL_TOOL_FORMAT_REJECTED: 'cause',
  MODEL_OUTPUT_UNFIT: 'cause',
  CRITERIA_NOT_MET: 'cause',
  NO_SUCCESSFUL_RESOURCE_EVIDENCE: 'cause',
  TIMEOUT: 'cause',
  PERMISSION_DENIED: 'cause',
  NOT_FOUND: 'cause',
  RATE_LIMITED: 'cause',
  NETWORK_ERROR: 'cause',
  SERVICE_UNAVAILABLE: 'cause',
  VALIDATION_ERROR: 'cause',
  TOKEN_LIMIT: 'cause',
  INTERNAL_ERROR: 'cause',
  SCHEMA_ERROR: 'cause',
  BUDGET: 'cause',
  STUCK_REPEATING: 'cause',
  REPEATED_FAILURE: 'cause',
  SCHEMA_CHURN: 'cause',
  REPEATED_GATE_REJECTION: 'cause',
  TASK_PARTIAL: 'cause',
  RUNNER_STOPPED: 'cause',
  CONNECTION_LOST: 'cause',
  HUMAN_NO_RESPONSE: 'cause',
  RUN_TIME_LIMIT: 'words',
  CONTEXT_LIMIT: 'words',
  UNGROUNDED_IDENTIFIER: 'cause',
  WORKSPACE_WRITE_FAILED: 'cause',
  WORKSPACE_UNAVAILABLE: 'cause',
  RESULT_EXPIRED: 'cause',
  SOURCE_READ_INCOMPLETE: 'words',
  PROVIDER_AUTH_FAILED: 'cause',
  MODEL_OUTPUT_TRUNCATED: 'cause',
  HOST_NOT_CONNECTED: 'cause',
  RUNNER_NOT_CONNECTED: 'cause',
  WORKFLOW_TOO_LARGE: 'cause',
  RUNNER_NEVER_ACCEPTED: 'cause',
  RUN_END_NOT_REPORTED: 'cause',
  LAUNCH_SETUP_REFUSED: 'cause',
  HOST_SETUP_REFUSED: 'cause',
  SOURCE_FILE_REFUSED: 'cause',
  HOST_DISCONNECTED: 'cause',
  IDLE_TIMEOUT: 'cause',
  SYSTEM_ONE_AGENT_NOT_EXECUTABLE: 'cause',
  TARGET_NOT_BOUND: 'cause',
  TARGET_NOT_AUTHORIZED: 'cause',
  INVALID_OPERATION: 'cause',
  INVALID_TRANSPORT: 'cause',
  MISSING_BINDINGS: 'cause',
  SCHEMA_CONTEXT: 'cause',
  UNSUPPORTED_ACTION: 'cause',
  OPERATION_OUTSIDE_SCOPE: 'cause',
} as const satisfies Record<RunEndRegistryCode, 'words' | 'next' | 'cause'>

type WordsOf<W> = { [C in RunEndRegistryCode]: (typeof END_WORDS)[C] extends W ? C : never }[RunEndRegistryCode]
/** A registry code with its own cause and next step. */
type WordedCode = WordsOf<'words'>
/** A registry code with its own next step. */
type NextCode = WordsOf<'words' | 'next'>

function wordedCode(code: string): WordedCode | undefined {
  return isRunEndRegistryCode(code) && END_WORDS[code] === 'words' ? (code as WordedCode) : undefined
}

function nextCode(code: string): NextCode | undefined {
  return isRunEndRegistryCode(code) && END_WORDS[code] !== 'cause' ? (code as NextCode) : undefined
}

/**
 * Reasons with their own words, by code (agion/Agion#3457, record Amendment
 * A3): the server stores agion-mcp's store failure reason beside
 * WORKSPACE_WRITE_FAILED. host_store_full (the host's store for results was
 * full with runs still in progress) has its own cause and next step,
 * `${K}.reason.<key>.title` and `.next`, which name who can check the limit
 * on the host's page; the chat links to no console page. Every other reason
 * keeps the code's words. The reasons are the generated list
 * (contract/execution StoreFailureReasons), so a renamed reason fails the
 * typecheck here. The console's twin is admin-ui src/services/run-end.ts.
 */
const REASON_WORDS: Record<Extract<RunEndRegistryCode, 'WORKSPACE_WRITE_FAILED'>, Partial<Record<StoreFailureReason, string>>> = {
  WORKSPACE_WRITE_FAILED: { host_store_full: 'hostStoreFull' },
}

function reasonKey(c: RunEndCode | undefined): string | undefined {
  if (c?.code !== 'WORKSPACE_WRITE_FAILED' || !isStoreFailureReason(c.reason)) return undefined
  return REASON_WORDS.WORKSPACE_WRITE_FAILED[c.reason]
}

/** Codes that name something the run did not have, and the words for it. */
const MISSING_KEYS: Record<string, string> = {
  CRITERIA_NOT_MET: 'criteria',
  NO_SUCCESSFUL_RESOURCE_EVIDENCE: 'resourceData',
  HUMAN_NO_RESPONSE: 'personAnswer',
  TASK_PARTIAL: 'stepOutputs',
  RUN_TIME_LIMIT: 'time',
  CONTEXT_LIMIT: 'room',
}

export interface RunEndReading {
  /** The server's result, which decides how the chat marks the line. */
  result: RunResult
  /** The code's own run ending term key, used when the chat has words for it. */
  termKey?: string
  /** The cause category's text key, used when the code has no term. */
  causeKey: string
  /** The code's own cause key; it reads ahead of any term. */
  ownCauseKey?: string
  /** What the reader can do next. */
  nextKey: string
  /** The stored code the cause names, in its uppercase form; absent when the server named none. */
  code?: string
  /** The text keys of what the run lacked, each once, in the stored order. */
  missingKeys: string[]
}

function record(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined
}

/** The end record from the run API's answer; null when the server sent no result (switch off). */
export function runEndRecordOf(execution: unknown): RunEndRecord | null {
  const run = record(execution)
  const result = run?.['result']
  if (typeof result !== 'string' || !RUN_RESULTS.includes(result)) return null
  const codes = Array.isArray(run?.['codes']) ? (run['codes'] as unknown[]) : []
  return {
    result: result as RunResult,
    codes: codes.flatMap((element) => {
      const c = record(element)
      if (typeof c?.['code'] !== 'string') return []
      return [{
        code: c['code'],
        effect: typeof c['effect'] === 'string' ? c['effect'] : '',
        cause: typeof c['cause'] === 'string' ? c['cause'] : '',
        resolved: c['resolved'] === true,
        ...(typeof c['reason'] === 'string' && c['reason'] ? { reason: c['reason'] } : {}),
      }]
    }),
  }
}

function firstUnresolved(codes: readonly RunEndCode[], effect: string): RunEndCode | undefined {
  return codes.find((c) => !c.resolved && c.effect === effect)
}

/**
 * Why the run ended and what it lacked; null for a completed run, for a
 * cancelled run with no unresolved failure or limitation (the stopped line
 * says it), and without an end record.
 */
export function runEndReading(end: RunEndRecord | null): RunEndReading | null {
  if (!end || end.result === 'completed') return null
  // The wire may carry a code in lowercase; every table reads the uppercase form.
  const codes = end.codes.map((c) => ({ ...c, code: c.code.toUpperCase() }))
  const first = firstUnresolved(codes, 'failure') ?? firstUnresolved(codes, 'limitation')
  if (!first && end.result === 'cancelled') return null
  const cause = causeKeyOf(first)
  const own = first ? wordedCode(first.code) : undefined
  const next = first ? nextCode(first.code) : undefined
  const missing = new Set<string>()
  for (const c of codes) {
    const item = MISSING_KEYS[c.code]
    if (!c.resolved && item) missing.add(item)
  }
  const termCode = first?.code.toLowerCase()
  const reason = reasonKey(first)
  const ownCauseKey = reason ? `${K}.reason.${reason}.title` : own ? `${K}.code.${own}.title` : undefined
  return {
    result: end.result,
    ...(termCode && /^[a-z_]+$/.test(termCode) ? { termKey: `chat:nextActivity.runEnding.${termCode}` } : {}),
    causeKey: `${K}.cause.${cause}`,
    ...(ownCauseKey ? { ownCauseKey } : {}),
    nextKey: reason ? `${K}.reason.${reason}.next` : next ? `${K}.code.${next}.next` : `${K}.next.${cause}`,
    missingKeys: [...missing].map((item) => `${K}.missingItem.${item}`),
    ...(first ? { code: first.code } : {}),
  }
}

type Translate = (key: string, options?: Record<string, unknown>) => string

/** Items as a list in the reader's language ("a, b and c" is the language's to word). */
function itemList(language: string, items: readonly string[]): string {
  // Intl.ListFormat is ES2021; reach it through a narrow type in case the lib predates it.
  const ListFormat = (Intl as unknown as { ListFormat?: new (l: string, o: { style: string; type: string }) => { format: (items: string[]) => string } }).ListFormat
  if (!ListFormat) return items.join(', ')
  try {
    return new ListFormat(language, { style: 'long', type: 'conjunction' }).format([...items])
  } catch {
    return items.join(', ')
  }
}

/**
 * A term written as a sentence, as it reads after "The run ended:". German
 * keeps its capital (a sentence after a colon starts with one), and so does
 * a term that opens with the product's name.
 */
export function midSentence(term: string, language: string): string {
  if (language.toLowerCase().startsWith('de') || /^Agion\b/.test(term)) return term
  return term.charAt(0).toLocaleLowerCase(language) + term.slice(1)
}

/**
 * The Activity row of a failed run: the term of the run's ending code (the
 * server's `outcome`), the same words the end message uses, else a plain
 * sentence. Never the run's raw error text.
 */
export function runFailedText(t: Translate, outcome: string | undefined): string {
  const code = typeof outcome === 'string' ? outcome.toLowerCase() : ''
  const term = /^[a-z_]+$/.test(code) ? t(`chat:nextActivity.runEnding.${code}`, { defaultValue: '' }) : ''
  return typeof term === 'string' && term !== '' ? term : t(`${K}.failed`)
}

/**
 * A run's failed ending in the Activity panel (agion/Agion#2946, #2947): the
 * chat's end message when the server sent an end record with a reason (the
 * end sentence, what to do next and, on its own line, the stored code), else
 * the Activity row's words for the stored code. Never a raw category or
 * error text.
 */
export function failedRunText(t: Translate, end: RunEndReading | null, code: string | undefined, language: string): string {
  if (!end) return runFailedText(t, code)
  const stored = runEndCodeText(t, end)
  return stored ? `${runEndLine(t, end, language)}\n${stored}` : runEndLine(t, end, language)
}

/**
 * The cut kinds (P-095 phase 3, Amendment A4, decisions 4 to 6, slice 11B-4):
 * the registry codes a run sends once on mission.ended when it shortened a
 * value at one of its limits (contract/workflow run_codes.go, effect
 * warning), each with its words. The generated run end codes hold only
 * failures and limitations, so this wording table is keyed by the registry
 * spelling; the
 * console's twin is admin-ui src/services/run-end.ts. `storage` marks the
 * kinds whose values workspace storage on the host keeps whole (decision 4).
 * RUN_LIMIT_BOUNDED is an authored run limit the run lowered to its maximum
 * (PLAN-20261013 slice 10, D9); it travels the same way.
 */
const CUT_KINDS: Record<string, { key: string; storage: boolean }> = {
  DELEGATION_HANDOFF_CUT: { key: 'delegationHandoff', storage: true },
  TASK_PROMPT_VALUE_CUT: { key: 'taskPromptValue', storage: true },
  CONTEXT_SHORTENED: { key: 'contextShortened', storage: false },
  RUN_STATE_TEXT_CUT: { key: 'runStateText', storage: true },
  VERDICT_EVIDENCE_CUT: { key: 'verdictEvidence', storage: true },
  HYDRATED_ROWS_CUT: { key: 'hydratedRows', storage: false },
  SCHEMA_TABLES_CUT: { key: 'schemaTables', storage: false },
  RUN_LIMIT_BOUNDED: { key: 'runLimitBounded', storage: false },
}

/** The note on values the run shortened, in the reader's words. */
export interface RunCutNote {
  title: string
  /** One line for each cut kind, once each, in the stored order. */
  lines: string[]
  /** The workspace storage line, when a listed kind's values it keeps whole. */
  storage?: string
}

/**
 * The note on values the run shortened: a title, one line for each cut kind
 * among the warning codes the server stored, and the storage line when it
 * applies. Null without an end record or without a cut kind; every other
 * warning code stays unshown, so a run that cut nothing reads as before. No
 * code is shown.
 */
export function runCutNote(t: Translate, end: RunEndRecord | null): RunCutNote | null {
  const seen = new Set<string>()
  const cuts: Array<{ key: string; storage: boolean }> = []
  for (const c of end?.codes ?? []) {
    // The wire may carry a code in lowercase; the table reads the uppercase form.
    const code = c.code.toUpperCase()
    const kind = CUT_KINDS[code]
    if (c.effect !== 'warning' || !kind || seen.has(code)) continue
    seen.add(code)
    cuts.push(kind)
  }
  if (cuts.length === 0) return null
  return {
    title: t(`${K}.cutNote.title`),
    lines: cuts.map((kind) => t(`${K}.cutNote.kind.${kind.key}`)),
    ...(cuts.some((kind) => kind.storage) ? { storage: t(`${K}.cutNote.storage`) } : {}),
  }
}

/** The end message: the cause sentence, then what was missing when anything was. */
export function runEndText(t: Translate, end: RunEndReading, language: string): string {
  const term = end.ownCauseKey ? t(end.ownCauseKey) : end.termKey ? t(end.termKey, { defaultValue: '' }) : ''
  const cause = typeof term === 'string' && term !== '' ? midSentence(term, language) : t(end.causeKey)
  const ended = t(`${K}.endedWithCause`, { cause })
  if (end.missingKeys.length === 0) return ended
  return `${ended} ${t(`${K}.missing`, { items: itemList(language, end.missingKeys.map((key) => t(key))) })}`
}

/** What the reader can do next, as one sentence. */
export function runEndNext(t: Translate, end: RunEndReading): string {
  return t(end.nextKey)
}

/** The chat's end line: the end message, then what the reader can do next. */
export function runEndLine(t: Translate, end: RunEndReading, language: string): string {
  return `${runEndText(t, end, language)} ${runEndNext(t, end)}`
}

/** The small-text line that shows the stored code; null when the server named none. */
export function runEndCodeText(t: Translate, end: RunEndReading): string | null {
  return end.code ? t(`${K}.codeLabel`, { code: end.code }) : null
}
